import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { User } from '../../../core/models/user.interface';
import { SUBSCRIPTION_TYPES } from '../../../core/shared/constants';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-mi-mentor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mi-mentor.component.html',
  styleUrl: './mi-mentor.component.scss'
})
export class MiMentorComponent implements OnInit {
  user: User | null = null;
  mentor: any = null;
  selectedDate: Date = new Date();
  calendarDate: Date = new Date(); // Fecha para navegación del calendario
  selectedTime: string = '';
  availableSlots: string[] = [];
  coachAvailability: any[] = [];
  coachAppointments: any[] = [];
  loading = false;
  showModal = false;
  appointmentNotes = '';

  // Propiedades para Phoenix Sessions
  isPhoenixSessions: boolean = false;
  phoenixSessionId: string = '';
  selectedTimes: string[] = []; // Para seleccionar 2 horarios consecutivos
  maxAppointments: number = 1;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  // Función para calcular la semana actual del usuario (basada en sidebar.component.ts)
  getCurrentWeek(): number {
    if (!this.user?.createdAt) {
      return 1;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.ceil((diffInDays + 1) / 7);
    
    return week;
  }

  // Función para actualizar el step5 con la phoenixSession correspondiente
  updatePhoenixSessionStep5(): Promise<any> {
    if (!this.user?._id || !this.isPhoenixSessions) {
      return Promise.resolve();
    }

    const currentWeek = this.getCurrentWeek();
    let phoenixSessionField = '';

    // Mapear la semana actual a la phoenixSession correspondiente
    switch (currentWeek) {
      case 4:
        phoenixSessionField = 'phoenixSessions3Completed';
        break;
      case 6:
        phoenixSessionField = 'phoenixSessions5Completed';
        break;
      case 1:
        phoenixSessionField = 'phoenixSessions1Completed';
        break;
      case 2:
        phoenixSessionField = 'phoenixSessions2Completed';
        break;
      case 3:
        phoenixSessionField = 'phoenixSessions3Completed';
        break;
      case 5:
        phoenixSessionField = 'phoenixSessions5Completed';
        break;
      case 7:
        phoenixSessionField = 'phoenixSessions7Completed';
        break;
      default:
        console.log(`Semana ${currentWeek} no tiene phoenixSession correspondiente`);
        return Promise.resolve();
    }

    if (!phoenixSessionField) {
      return Promise.resolve();
    }

    // Preparar los datos para actualizar el step5
    const updateData = {
      phoenixSessionField: phoenixSessionField,
      completedAt: new Date().toISOString()
    };

    console.log(`Actualizando step5 para semana ${currentWeek}:`, updateData);

    // Actualizar en el backend usando el nuevo endpoint
    return this.http.post(`${environment.apiUrl}/users/${this.user._id}/step5`, updateData).toPromise();
  }

  ngOnInit() {
    this.user = this.authService.getUser();
    
    // Detectar parámetros de Phoenix Sessions
    this.route.queryParams.subscribe(params => {
      if (params['phoenixSessions'] === 'true') {
        this.isPhoenixSessions = true;
        this.phoenixSessionId = params['sessionId'] || '';
        this.maxAppointments = parseInt(params['maxAppointments']) || 2;
        console.log('Phoenix Sessions detectado:', {
          sessionId: this.phoenixSessionId,
          maxAppointments: this.maxAppointments,
          source: params['source']
        });
      }
    });
    
    if (this.user && this.user.coachAssigned) {
      this.loadMentorData();
    }
  }

  loadMentorData() {
    if (this.user?.coachAssigned?.id) {
      this.loading = true;
      
      // Hacer consulta al backend para obtener los datos específicos del mentor
      this.http.get(`${environment.apiUrl}/users/${this.user.coachAssigned.id}/mentor-info`)
        .subscribe({
          next: (response: any) => {
            this.mentor = response.data;
            
            // Procesar los datos de disponibilidad y citas
            if (response.data.coachAvailability) {
              this.coachAvailability = response.data.coachAvailability;
            }
            
            if (response.data.coachAppointments) {
              this.coachAppointments = response.data.coachAppointments;
            }
            
            // Actualizar los horarios disponibles para la fecha seleccionada
            this.updateAvailableSlots();
            
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al cargar datos del mentor:', error);
            this.loading = false;
            // Fallback: usar los datos básicos que ya tenemos
            this.mentor = this.user?.coachAssigned;
            this.coachAvailability = [];
            this.coachAppointments = [];
            this.availableSlots = [];
          }
        });
    }
  }


  selectDate(date: Date) {
    this.selectedDate = new Date(date.getTime());
    this.selectedTime = '';
    this.selectedTimes = []; // Limpiar selección múltiple
    this.updateAvailableSlots();
    this.cdr.detectChanges();
  }

  selectTime(time: string) {
    if (this.isPhoenixSessions) {
      // Para Phoenix Sessions, permitir seleccionar hasta 2 horarios consecutivos
      if (this.selectedTimes.includes(time)) {
        // Si ya está seleccionado, lo removemos
        this.selectedTimes = this.selectedTimes.filter(t => t !== time);
      } else if (this.selectedTimes.length < this.maxAppointments) {
        // Si no está seleccionado y no hemos alcanzado el máximo, lo agregamos
        this.selectedTimes.push(time);
        this.selectedTimes.sort(); // Ordenar para mantener consistencia
      } else {
        // Si ya tenemos el máximo, reemplazamos el primero
        this.selectedTimes = [time];
      }
      
      // Para compatibilidad con el código existente
      this.selectedTime = this.selectedTimes[0] || '';
    } else {
      // Comportamiento normal para citas regulares
      this.selectedTime = time;
    }
  }

  scheduleAppointment() {
    // Validación para Phoenix Sessions
    if (this.isPhoenixSessions) {
      if (this.selectedTimes.length !== this.maxAppointments) {
        Swal.fire({
          icon: 'warning',
          title: 'Horarios requeridos',
          text: `Por favor selecciona exactamente ${this.maxAppointments} horarios consecutivos para Phoenix Sessions`,
          confirmButtonText: 'Entendido'
        });
        return;
      }
    } else {
      // Validación para citas regulares
      if (!this.selectedTime) {
        Swal.fire({
          icon: 'warning',
          title: 'Horario requerido',
          text: 'Por favor selecciona un horario',
          confirmButtonText: 'Entendido'
        });
        return;
      }
    }

    if (this.availableSlots.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin disponibilidad',
        text: 'No hay horarios disponibles para la fecha seleccionada',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Abrir el modal de confirmación
    this.showModal = true;
  }

  cancel() {
    // Lógica para cancelar
    this.selectedTime = '';
  }

  closeModal() {
    this.showModal = false;
    this.appointmentNotes = '';
  }

  confirmAppointment() {
    // Validación para Phoenix Sessions
    if (this.isPhoenixSessions) {
      if (this.selectedTimes.length !== this.maxAppointments) {
        Swal.fire({
          icon: 'warning',
          title: 'Horarios requeridos',
          text: `Por favor selecciona exactamente ${this.maxAppointments} horarios consecutivos para Phoenix Sessions`,
          confirmButtonText: 'Entendido'
        });
        return;
      }
    } else {
      // Validación para citas regulares
      if (!this.selectedDate || !this.selectedTime) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: 'Por favor selecciona una fecha y hora',
          confirmButtonText: 'Entendido'
        });
        return;
      }
    }

    if (!this.user?._id) {
      Swal.fire({
        icon: 'error',
        title: 'Error de usuario',
        text: 'Usuario no identificado',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    if (!this.mentor?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error de mentor',
        text: 'Mentor no identificado',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Preparar los datos según el schema de appointments
    let appointmentsData: any[] = [];
    
    if (this.isPhoenixSessions) {
      // Para Phoenix Sessions, crear múltiples citas
      appointmentsData = this.selectedTimes.map((time, index) => ({
        date: this.formatDateForAPI(this.selectedDate),
        time: this.convertTimeTo24Hour(time),
        dateValue: this.selectedDate.toISOString(),
        customer: {
          id: this.user!._id,
          name: `${this.user!.firstName || ''} ${this.user!.lastName || ''}`.trim()
        },
        coach: {
          id: this.mentor.id,
          name: `${this.mentor.coachProfileData?.firstName || ''} ${this.mentor.coachProfileData?.lastName || ''}`.trim(),
          type: {
            id: 1,
            name: 'Mentor'
          }
        },
        // Agregar información específica de Phoenix Sessions
        phoenixSessions: true,
        sessionId: this.phoenixSessionId,
        sessionPart: index + 1, // Parte 1, 2, etc.
        totalParts: this.maxAppointments
      }));
    } else {
      // Para citas regulares, crear una sola cita
      appointmentsData = [{
        date: this.formatDateForAPI(this.selectedDate),
        time: this.convertTimeTo24Hour(this.selectedTime),
        dateValue: this.selectedDate.toISOString(),
        customer: {
          id: this.user!._id,
          name: `${this.user!.firstName || ''} ${this.user!.lastName || ''}`.trim()
        },
        coach: {
          id: this.mentor.id,
          name: `${this.mentor.coachProfileData?.firstName || ''} ${this.mentor.coachProfileData?.lastName || ''}`.trim(),
          type: {
            id: 1,
            name: 'Mentor'
          }
        }
      }];
    }

    // Validar que todos los campos requeridos estén presentes
    const firstAppointment = appointmentsData[0];
    if (!firstAppointment.date || !firstAppointment.time || !firstAppointment.dateValue) {
      Swal.fire({
        icon: 'error',
        title: 'Datos faltantes',
        text: 'Faltan datos de fecha y hora',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    if (!firstAppointment.customer.id || !firstAppointment.customer.name) {
      Swal.fire({
        icon: 'error',
        title: 'Datos del cliente faltantes',
        text: 'Faltan datos del cliente',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    if (!firstAppointment.coach.id || !firstAppointment.coach.name) {
      Swal.fire({
        icon: 'error',
        title: 'Datos del mentor faltantes',
        text: 'Faltan datos del mentor',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    console.log('Enviando citas al backend:', appointmentsData);
    console.log('Número de citas:', appointmentsData.length);
    console.log('Es Phoenix Sessions:', this.isPhoenixSessions);

    this.loading = true;
    
    // Función para crear las citas
    const createAppointments = (): Promise<any[]> => {
      const appointmentPromises = appointmentsData.map(appointment => 
        this.http.post(`${environment.apiUrl}/appointments`, appointment).toPromise()
      );
      
      return Promise.all(appointmentPromises);
    };
    
    // Función para actualizar el permiso de Phoenix Sessions
    const updatePhoenixSessionsPermission = () => {
      if (this.isPhoenixSessions && this.user?.permissions) {
        const permission4 = this.user.permissions.find((p: any) => p.id === "4");
        if (permission4) {
          permission4.active = true;
          permission4.updatedAt = new Date().toISOString();
          // No agregar updatedBy ya que no existe en el tipo
          
          // Actualizar en el backend
          return this.http.patch(`${environment.apiUrl}/users/${this.user!._id}/permissions`, this.user.permissions).toPromise();
        }
      }
      return Promise.resolve();
    };
    
    // Ejecutar creación de citas y actualización de permiso
    createAppointments()
      .then((responses: any[]) => {
        console.log('Respuestas del backend:', responses);
        
        // Verificar que todas las citas se crearon exitosamente
        const allSuccessful = responses.every(response => response.success);
        
        if (allSuccessful) {
          console.log('Todas las citas creadas exitosamente');
          
          // Actualizar el permiso de Phoenix Sessions si es necesario
          return updatePhoenixSessionsPermission().then(() => {
            // Actualizar el step5 con la phoenixSession correspondiente
            return this.updatePhoenixSessionStep5();
          }).then(() => {
            console.log('Todas las actualizaciones completadas, mostrando modal');
            this.showSuccessModal();
          }).catch((error) => {
            console.error('Error en actualizaciones:', error);
            // Aún así mostrar el modal de éxito
            this.showSuccessModal();
          });
        } else {
          // Manejar error en alguna de las citas
          this.loading = false;
          const errorMessage = 'Error al agendar una o más citas. Por favor, intenta nuevamente.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
            confirmButtonText: 'Entendido'
          });
          return Promise.resolve();
        }
      })
      .catch((error) => {
        console.error('Error al crear citas:', error);
        this.loading = false;
        const errorMessage = 'Error al agendar las citas. Por favor, intenta nuevamente.';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonText: 'Entendido'
        });
      });
  }

  private showSuccessModal(): void {
    this.loading = false;
    this.showModal = false;
    this.appointmentNotes = '';
    this.selectedTime = '';
    this.selectedTimes = [];
    
    // Mostrar mensaje de éxito
    const successMessage = this.isPhoenixSessions 
      ? `¡Phoenix Sessions confirmadas! Se han agendado 2 citas de 1 hora cada una. Recibirás un correo con todos los detalles.`
      : 'Tu sesión con el mentor ha sido confirmada. Recibirás un correo con todos los detalles de la cita y las instrucciones de acceso.';
    
    console.log('Mostrando modal de éxito, isPhoenixSessions:', this.isPhoenixSessions);
    
    Swal.fire({
      title: '¡Reserva exitosa!',
      html: successMessage,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Ver en calendario',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-content',
        confirmButton: 'swal-custom-confirm',
        cancelButton: 'swal-custom-cancel'
      }
    }).then((result) => {
      console.log('Resultado del modal:', result);
      console.log('isPhoenixSessions:', this.isPhoenixSessions);
      
      if (result.isConfirmed) {
        // Para Phoenix Sessions, recargar la página completamente sin parámetros
        if (this.isPhoenixSessions) {
          console.log('Redirigiendo Phoenix Sessions a /dashboard/mi-mentor');
          window.location.href = '/dashboard/mi-mentor';
        } else {
          // Para citas regulares, redirigir al dashboard
          console.log('Redirigiendo cita regular al dashboard');
          window.location.href = this.getDashboardUrl();
        }
      } else {
        // Si no confirma, también redirigir para Phoenix Sessions
        if (this.isPhoenixSessions) {
          console.log('Usuario canceló, pero es Phoenix Sessions, redirigiendo igual');
          window.location.href = '/dashboard/mi-mentor';
        }
      }
    });
    
    // Opcional: recargar los datos del mentor para actualizar disponibilidad
    this.loadMentorData();
  }

  private convertTimeTo24Hour(time12h: string): string {
    // Si ya está en formato 12h con AM/PM, mantenerlo así
    if (time12h.includes('AM') || time12h.includes('PM')) {
      console.log('Hora ya en formato 12h:', time12h);
      return time12h;
    }
    
    // Si está en formato 24h, convertir a 12h
    const [hours, minutes] = time12h.split(':');
    const hour = parseInt(hours, 10);
    
    if (hour === 0) {
      return `12:${minutes} AM`;
    } else if (hour < 12) {
      return `${hour}:${minutes} AM`;
    } else if (hour === 12) {
      return `12:${minutes} PM`;
    } else {
      return `${hour - 12}:${minutes} PM`;
    }
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/img/user-photo-default.png';
  }

  getCalendarDates(): Date[] {
    const currentMonth = this.calendarDate.getMonth();
    const currentYear = this.calendarDate.getFullYear();
    
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    const dates: Date[] = [];
    
    // Agregar días del mes anterior
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0);
    const startDate = firstDay.getDay() - 1; // Lunes = 0
    
    for (let i = startDate; i >= 0; i--) {
      dates.push(new Date(currentYear, currentMonth - 1, prevMonthLastDay.getDate() - i));
    }
    
    // Agregar días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      dates.push(new Date(currentYear, currentMonth, day));
    }
    
    // Agregar días del siguiente mes para completar la semana
    const remainingDays = 42 - dates.length; // 6 semanas x 7 días
    for (let day = 1; day <= remainingDays; day++) {
      dates.push(new Date(currentYear, currentMonth + 1, day));
    }
    
    return dates;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isSelectedDate(date: Date): boolean {
    return date.toDateString() === this.selectedDate.toDateString();
  }

  isAvailableDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Verificar que la fecha no sea del pasado
    if (date < today) {
      return false;
    }
    
    // Verificar que haya horarios disponibles para esta fecha
    return this.hasAvailableSlots(date);
  }

  getMonthName(date: Date): string {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[date.getMonth()];
  }

  getYear(date: Date): number {
    return date.getFullYear();
  }

  getDayName(index: number): string {
    const days = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
    return days[index];
  }

  /**
   * Navega al mes anterior en el calendario
   */
  previousMonth() {
    const newDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // No permitir navegar a meses muy lejanos en el pasado
    if (newDate >= new Date(today.getFullYear(), today.getMonth() - 12, 1)) {
      this.calendarDate = newDate;
    }
  }

  /**
   * Navega al mes siguiente en el calendario
   */
  nextMonth() {
    const newDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1);
    const today = new Date();
    
    // No permitir navegar más de 12 meses en el futuro
    if (newDate <= new Date(today.getFullYear() + 1, today.getMonth(), 1)) {
      this.calendarDate = newDate;
    }
  }

  /**
   * Actualiza los horarios disponibles basándose en la fecha seleccionada
   */
  updateAvailableSlots() {
    if (!this.coachAvailability || this.coachAvailability.length === 0) {
      this.availableSlots = [];
      return;
    }

    const selectedDateStr = this.formatDateForAPI(this.selectedDate);
    
    // Buscar disponibilidad para esta fecha
    const dayAvailability = this.coachAvailability.find(
      (availability: any) => availability.day === selectedDateStr
    );

    if (!dayAvailability || !dayAvailability.times) {
      this.availableSlots = [];
      return;
    }

    // Obtener citas reservadas
    const bookedTimes = this.coachAppointments
      .filter((appointment: any) => appointment.date === selectedDateStr)
      .map((appointment: any) => this.normalizeTimeFormat(appointment.time));

    // Filtrar horarios disponibles
    const availableTimes = dayAvailability.times.filter(
      (time: string) => !bookedTimes.includes(this.normalizeTimeFormat(time))
    );

    // Convertir a formato 12h
    this.availableSlots = availableTimes.map((time: string) => (time.toString()));
  }

  /**
   * Normaliza el formato de tiempo para comparaciones consistentes
   * Convierte "2:00 PM" a "02:00 PM" para mantener consistencia
   */
  private normalizeTimeFormat(time: string): string {
    if (!time) return time;
    
    // Dividir por el espacio para separar tiempo y AM/PM
    const parts = time.split(' ');
    if (parts.length !== 2) return time;
    
    const [timePart, period] = parts;
    const [hours, minutes] = timePart.split(':');
    
    // Normalizar las horas con padding de cero
    const normalizedHours = hours.padStart(2, '0');
    
    return `${normalizedHours}:${minutes} ${period}`;
  }

  /**
   * Convierte una fecha a formato YYYY-MM-DD para comparar con la API
   */
  private formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Convierte formato de 24h (HH:MM) a 12h (H:MM am/pm)
   */
  private formatTimeTo12Hour(time24: string): string {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    
    if (hour === 0) {
      return `12:${minutes}`;
    } else if (hour < 12) {
      return `${hour}:${minutes}`;
    } else if (hour === 12) {
      return `12:${minutes}`;
    } else {
      return `${hour - 12}:${minutes}`;
    }
  }

  /**
   * Verifica si una fecha tiene horarios disponibles
   */
  hasAvailableSlots(date: Date): boolean {
    const dateStr = this.formatDateForAPI(date);
    const dayAvailability = this.coachAvailability.find(
      (availability: any) => availability.day === dateStr
    );

    if (!dayAvailability || !dayAvailability.times) {
      return false;
    }

    const bookedTimes = this.coachAppointments
      .filter((appointment: any) => appointment.date === dateStr)
      .map((appointment: any) => appointment.time);

    return dayAvailability.times.some((time: string) => !bookedTimes.includes(time));
  }

  /**
   * Verifica si el botón de mes anterior debe estar deshabilitado
   */
  isPreviousMonthDisabled(): boolean {
    const today = new Date();
    const minDate = new Date(today.getFullYear(), today.getMonth() - 12, 1);
    return this.calendarDate <= minDate;
  }

  /**
   * Verifica si el botón de mes siguiente debe estar deshabilitado
   */
  isNextMonthDisabled(): boolean {
    const today = new Date();
    const maxDate = new Date(today.getFullYear() + 1, today.getMonth(), 1);
    return this.calendarDate >= maxDate;
  }

  // Método de debug para mostrar el modal de confirmación
  showDebugModal() {
    Swal.fire({
      title: '¡Reserva exitosa!',
      html: 'Tu sesión con el mentor ha sido confirmada. Recibirás un correo con todos los detalles de la cita y las instrucciones de acceso.',
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Ver en calendario',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-content',
        confirmButton: 'swal-custom-confirm',
        cancelButton: 'swal-custom-cancel'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirigir al dashboard
        window.location.href = this.getDashboardUrl();
      }
    });
  }

  /**
   * Verifica si el usuario es Executive
   */
  isExecutiveUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  /**
   * Obtiene la URL correcta para el dashboard
   */
  getDashboardUrl(): string {
    return this.isExecutiveUser() ? '/dashboard/dashboard-executive' : '/dashboard/dashboard-menu';
  }

}
